// Difficulty director: ramps spawn rate over time, scales enemy HP/damage by the
// minute, unlocks tougher enemy types, and fires a timed schedule of named events
// (burst spawns) and bosses (incl. the Catalyst run-ender). Spawns on a ring around
// the player at a fixed distance so runs stay deterministic (plan §3.8).

import { Pool } from '../core/pool';
import { clamp, lerp } from '../core/math';
import { ARENA } from './balance';
import { spawnBoss, spawnEnemy } from './enemies';
import type { Enemy } from './enemies';
import type { EnemyType } from './balance';
import type { Rng } from '../core/rng';
import type { LevelDefinition } from './levels';

export interface SpawnResult {
  banner: string | null; // a named event/boss to announce this tick
  boss: Enemy | null; // a boss that just spawned (for the health bar)
  bloomWallCount: number; // one-shot terrain pulse requested by the level schedule
  gravityRailCount: number; // one-shot directional terrain pulse requested by the level schedule
}

export class Spawner {
  private acc = 0;
  private fired = 0; // index into the current level schedule
  private spawnX = 0; // ring() output (reused, no per-spawn allocation)
  private spawnY = 0;
  private readonly result: SpawnResult = { banner: null, boss: null, bloomWallCount: 0, gravityRailCount: 0 };

  constructor(
    private readonly rng: Rng,
    private readonly level: LevelDefinition,
  ) {}

  update(
    dt: number,
    elapsedSec: number,
    px: number,
    py: number,
    enemies: Pool<Enemy>,
    spawnMul: number,
    enemyHpMul = 1,
  ): SpawnResult {
    const tuning = this.level.spawnTuning;
    const minutes = elapsedSec / 60;
    const hpScale = (1 + tuning.hpScalePerMin * minutes) * enemyHpMul;
    const dmgScale = 1 + tuning.dmgScalePerMin * minutes;

    this.result.banner = null;
    this.result.boss = null;
    this.result.bloomWallCount = 0;
    this.result.gravityRailCount = 0;

    // Timed schedule: bosses + named escalation events.
    while (this.fired < this.level.schedule.length && elapsedSec >= this.level.schedule[this.fired].t) {
      const ev = this.level.schedule[this.fired++];
      this.result.banner = ev.banner;
      if (ev.kind === 'boss' && ev.bossKey !== undefined) {
        for (let k = 0; k < (ev.count ?? 1); k++) {
          this.ring(px, py);
          const b = spawnBoss(enemies, this.enemyType(this.level.bossTypes, ev.bossKey, 'boss'), this.spawnX, this.spawnY, hpScale);
          if (b !== null) this.result.boss = b;
        }
      } else if (ev.kind === 'event' && ev.burst !== undefined) {
        const burst = Math.round(ev.burst * spawnMul); // easy mode → smaller bursts
        for (let k = 0; k < burst && enemies.count < tuning.maxActive; k++) {
          this.ring(px, py);
          spawnEnemy(enemies, this.pickType(elapsedSec), this.spawnX, this.spawnY, hpScale, dmgScale, this.rng.next());
        }
      } else if (ev.kind === 'bloom') {
        this.result.bloomWallCount += ev.bloomWallCount ?? 0;
      } else if (ev.kind === 'rail') {
        this.result.gravityRailCount += ev.gravityRailCount ?? 0;
      }
    }

    // Regular ramping stream (scaled by difficulty - bosses above are never scaled).
    const rate =
      lerp(tuning.baseSpawnPerSec, tuning.maxSpawnPerSec, clamp(elapsedSec / tuning.rampToMaxSec, 0, 1)) *
      spawnMul;
    this.acc += rate * dt;
    let spawned = 0;
    while (this.acc >= 1 && spawned < tuning.maxPerTick && enemies.count < tuning.maxActive) {
      this.acc -= 1;
      spawned++;
      this.ring(px, py);
      spawnEnemy(enemies, this.pickType(elapsedSec), this.spawnX, this.spawnY, hpScale, dmgScale, this.rng.next());
    }
    if (this.acc > tuning.maxPerTick) this.acc = tuning.maxPerTick;

    return this.result;
  }

  // Pick a spawn point on the off-screen ring around the player → this.spawnX/Y.
  private ring(px: number, py: number): void {
    const angle = this.rng.range(0, Math.PI * 2);
    const { distancePx, distanceJitterPx } = this.level.spawnTuning;
    const dist = distancePx + this.rng.range(-distanceJitterPx, distanceJitterPx);
    this.spawnX = clamp(px + Math.cos(angle) * dist, 0, ARENA.width);
    this.spawnY = clamp(py + Math.sin(angle) * dist, 0, ARENA.height);
  }

  private pickType(elapsedSec: number): EnemyType {
    let total = 0;
    for (const u of this.level.tierUnlocks) if (elapsedSec >= u.t) total += u.w;
    let r = this.rng.next() * total;
    for (const u of this.level.tierUnlocks) {
      if (elapsedSec < u.t) continue;
      r -= u.w;
      if (r <= 0) return this.enemyType(this.level.enemyTypes, u.key, 'enemy');
    }
    throw new Error(`Level ${this.level.id} has no unlocked enemy at ${elapsedSec.toFixed(3)} seconds`);
  }

  private enemyType(types: Readonly<Record<string, EnemyType>>, key: string, group: 'enemy' | 'boss'): EnemyType {
    const type = types[key];
    if (type === undefined) throw new Error(`Level ${this.level.id} references unknown ${group} type: ${key}`);
    return type;
  }
}
